'use client';

import type { MouseEvent } from 'react';
import { useRef } from 'react';
import { DRIVE_APP_ICON_SRC } from '@frameio/shared/resources/mounted-storage/constants';
import { Button2, IconButton2, Text } from '@frameio/vapor';
import { IconCloseFill } from '@frameio/vapor-icons';
import styles from './DriveFloatingToast.module.css';
import TEXT from './generated/TEXT/DriveFloatingToast';

TEXT.DEFINE({
  /** Accessible label for the button that dismisses the Frame.io Drive toast */
  dismissLabel: 'Dismiss',
  /** Frame.io Drive toast body copy; "NEW" is emphasized */
  message: (
    <>
      <strong>NEW</strong> Mount this project directly on your desktop.
    </>
  ),
  /** Label for the button that takes the user to get Frame.io Drive */
  ctaLabel: 'Get Frame.io Drive',
  /** Alt text for the Frame.io Drive app icon */
  iconAlt: 'Frame.io Drive',
});

export type DriveFloatingToastProps = {
  /** Fired when the call-to-action button is pressed */
  onCtaClick: () => void;
  /** Fired when the dismiss (×) button is pressed */
  onDismiss: () => void;
};

export function DriveFloatingToast({
  onCtaClick,
  onDismiss,
}: DriveFloatingToastProps) {
  const cardRef = useRef<HTMLDivElement>(null);

  const handleMouseMove = (event: MouseEvent<HTMLDivElement>) => {
    const card = cardRef.current;
    if (!card) return;
    const rect = card.getBoundingClientRect();
    card.style.setProperty('--spotlight-x', `${event.clientX - rect.left}px`);
    card.style.setProperty('--spotlight-y', `${event.clientY - rect.top}px`);
  };

  return (
    <div ref={cardRef} className={styles.card} onMouseMove={handleMouseMove}>
      <div aria-hidden={true} className={styles.glow} />
      <div aria-hidden={true} className={styles.spotlight} />
      <div className={styles.dismissButton}>
        <IconButton2
          icon={
            <IconCloseFill style={{ color: 'var(--colors-icon-secondary)' }} />
          }
          label={TEXT.dismissLabel}
          onPress={onDismiss}
          size="small"
          variant="tertiary"
        />
      </div>
      <div className={styles.header}>
        <img
          alt={TEXT.iconAlt}
          className={styles.icon}
          src={DRIVE_APP_ICON_SRC}
        />
        <Text color="body" variant="body">
          {TEXT.message}
        </Text>
      </div>
      <div className={styles.cta}>
        <Button2
          block={true}
          onPress={onCtaClick}
          size="small"
          variant="secondary"
        >
          {TEXT.ctaLabel}
        </Button2>
      </div>
    </div>
  );
}
